class MirrorStory extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.scenes = new Map([...this.querySelectorAll('[data-story-image]')].map(image => [image.dataset.storyImage, image]));
    this.chapters = [...this.querySelectorAll('[data-story-chapter]')];
    this.visual = this.querySelector('.mirror-story__visual');
    this.message = this.querySelector('[data-story-message]');
    this.desktop = matchMedia('(min-width: 64rem)');
    this.requestVersion = (this.requestVersion || 0) + 1;
    this.editorScene = null;
    this.currentScene = this.querySelector('[data-story-image][data-story-state="active"]')?.dataset.storyImage || this.scenes.keys().next().value;

    for (const [id, image] of this.scenes) {
      image.addEventListener('load', () => {
        if (id === this.currentScene) this.completeScene(id, this.requestVersion);
      }, { signal });
      image.addEventListener('error', () => {
        if (id === this.currentScene) this.paintScene(id, true);
      }, { signal });
    }
    this.addEventListener('shopify:block:select', event => {
      const chapter = event.target.closest('[data-story-chapter]');
      if (!chapter) return;
      this.editorScene = chapter.dataset.storyChapter;
      this.selectScene(this.editorScene, true);
    }, { signal });
    this.addEventListener('shopify:block:deselect', () => {
      this.editorScene = null;
      this.observeChapters(true);
    }, { signal });
    this.desktop.addEventListener('change', () => this.observeChapters(), { signal });
    window.addEventListener('resize', () => this.observeChapters(), { signal, passive: true });
    document.addEventListener('header:geometry', () => this.observeChapters(), { signal });
    if (this.currentScene) this.selectScene(this.currentScene);
    this.observeChapters();
    this.setAttribute('data-enhanced', '');
  }

  observeChapters(force = false) {
    if (!('IntersectionObserver' in window)) return;
    // The mobile stage includes the header clearance inside its sticky box.
    const occupied = this.desktop.matches ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-occupied')) || 0 : 0;
    const mediaHeight = this.desktop.matches ? 0 : this.visual?.offsetHeight || 0;
    const viewportHeight = Math.max(1, innerHeight);
    const available = Math.max(0, viewportHeight - occupied - mediaHeight);
    const marker = Math.max(0, Math.min(viewportHeight - 1, Math.round(occupied + mediaHeight + available * 0.35)));
    const rootMargin = `-${marker}px 0px -${viewportHeight - marker - 1}px 0px`;
    if (!force && this.observer && rootMargin === this.observerMargin) return;
    this.observer?.disconnect();
    this.observerMargin = rootMargin;
    this.observer = new IntersectionObserver(entries => {
      if (this.editorScene) return;
      const current = entries.find(entry => entry.isIntersecting);
      if (current) this.selectScene(current.target.dataset.storyChapter, true);
    }, { rootMargin, threshold: 0 });
    for (const chapter of this.chapters) this.observer.observe(chapter);
  }

  selectScene(id, eager = false) {
    const image = this.scenes.get(id);
    if (!image) return;
    if (id === this.currentScene && this.readyScene === id) return;
    this.currentScene = id;
    const version = ++this.requestVersion;
    if (!image.complete) {
      if (!eager) return;
      image.loading = 'eager';
      this.setMessage('loading');
    }
    this.completeScene(id, version);
  }

  async completeScene(id, version) {
    const image = this.scenes.get(id);
    try {
      await image.decode();
    } catch {
      // A responsive source can change during decode; a loaded image remains usable.
      if (!image.complete) return;
    }
    if (!this.isConnected || version !== this.requestVersion) return;
    this.paintScene(id, !image.naturalWidth);
  }

  paintScene(id, failed) {
    if (failed) {
      this.setMessage('error');
      return;
    }
    for (const [sceneId, image] of this.scenes) {
      const active = sceneId === id;
      image.dataset.storyState = active ? 'active' : 'inactive';
      image.setAttribute('aria-hidden', String(!active));
    }
    this.readyScene = id;
    for (const chapter of this.chapters) {
      chapter.toggleAttribute('data-story-current', chapter.dataset.storyChapter === id);
    }
    this.setMessage(null);
  }

  setMessage(state) {
    if (!this.message) return;
    this.message.hidden = !state;
    this.message.textContent = state ? this.message.dataset[state] : '';
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.observer?.disconnect();
    this.observer = null;
    this.requestVersion += 1;
    this.readyScene = null;
    this.removeAttribute('data-enhanced');
  }
}

if (!customElements.get('mirror-story')) customElements.define('mirror-story', MirrorStory);
